/**
 * Parses a D&D Beyond character sheet PDF (fillable form) into a
 * structure matching the backend ImportedSheet shape.
 *
 * DDB PDFs are fillable AcroForms — all character data lives in named
 * form field annotations, not in the text stream.  We use pdfjs-dist's
 * getAnnotations() to read them, so no server-side tooling is needed.
 */

import * as pdfjsLib from 'pdfjs-dist';

// Point at the bundled worker.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url,
).toString();

// ────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────

import type { SpellEntry } from './character';

/** Mirrors the backend ImportedSheet. */
export interface ParsedSheet {
  className: string;
  subclass?: string;
  level: number;
  hitDieTotal: number;
  baseStats: Record<string, number>;
  asiDeltas: Record<string, number>;
  featDeltas: Record<string, number>;
  proficiencies: Record<string, string>;
  feats: string[];
  spells: SpellEntry[];
  inventory: { name: string; qty: number }[];

  // ── Character-level metadata ──
  race?: string;
  background?: string;
  alignment?: string;
  walkSpeed?: number;
  languages?: string[];
  personalityTraits?: string;
  ideals?: string;
  bonds?: string;
  flaws?: string;

  // ── Import-only metadata (informational) ──
  armorClass?: number;
  spellcastingAbility?: string;
  spellSaveDc?: number;
  spellAttackBonus?: number;

  warnings: string[];
}

// ────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────

/**
 * Parse a DDB character sheet PDF (as an ArrayBuffer from a file input)
 * into a ParsedSheet suitable for the "parsed" platform import endpoint.
 */
export async function parseDdbPdf(buffer: ArrayBuffer): Promise<ParsedSheet> {
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

  // Collect all form field annotations across all pages into a flat map.
  const fields: Record<string, string> = {};
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const annotations = await page.getAnnotations();
    for (const a of annotations) {
      if (a.fieldName && a.fieldValue != null && a.fieldValue !== '') {
        fields[a.fieldName.trim()] = String(a.fieldValue);
      }
    }
  }

  // Also extract raw text content from each page — used as a spell fallback
  // when the PDF has spells rendered as text rather than form fields.
  const pageTexts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const text = tc.items.map((item: any) => item.str ?? '').join(' ');
    pageTexts.push(text);
  }

  // Log all field names for debugging spell/inventory parsing issues.
  console.info('[PDF import] form field names:', Object.keys(fields).sort());

  const warnings: string[] = [];

  // ── Class & Level ──
  const classLevelRaw = fields['CLASS  LEVEL'] ?? fields['CLASS LEVEL'] ?? '';
  const classLevel = parseClassLevel(classLevelRaw);
  if (!classLevel) {
    throw new Error(
      `Could not parse class & level from "${classLevelRaw}". Is this a D&D Beyond character sheet?`,
    );
  }

  // ── Ability scores ──
  const baseStats: Record<string, number> = {};
  for (const key of ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'] as const) {
    const raw = fields[key];
    if (raw) {
      const val = parseInt(raw, 10);
      if (!isNaN(val) && val >= 1 && val <= 30) baseStats[key] = val;
    }
  }
  if (Object.keys(baseStats).length < 6) {
    warnings.push(
      `Only found ${Object.keys(baseStats).length}/6 ability scores — check base stats after import`,
    );
  }

  // ── Max HP + Hit Dice ──
  const maxHp = parseInt(fields['MaxHP'] ?? '', 10) || null;
  const hitDice = parseHitDice(fields['Total'] ?? '');
  const conMod = Math.floor(((baseStats.CON ?? 10) - 10) / 2);

  let hitDieTotal: number;
  if (maxHp != null) {
    // hitDieTotal = maxHp - level * conMod (the backend formula)
    hitDieTotal = maxHp - classLevel.level * conMod;
  } else if (hitDice) {
    hitDieTotal = hitDice.count * hitDice.die; // rough estimate
    warnings.push('MaxHP field missing — hitDieTotal is estimated from hit dice');
  } else {
    const classDie = CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
    hitDieTotal = classDie * classLevel.level;
    warnings.push('MaxHP and hit dice fields missing — hitDieTotal is a rough estimate');
  }

  // Clamp to valid range.
  const classDie = hitDice?.die ?? CLASS_HIT_DICE[classLevel.className.toLowerCase()] ?? 8;
  hitDieTotal = Math.max(classLevel.level, Math.min(classLevel.level * classDie, hitDieTotal));

  // ── Saving throw proficiencies ──
  const saveProfKeys: [string, string][] = [
    ['StrProf', 'strength'],
    ['DexProf', 'dexterity'],
    ['ConProf', 'constitution'],
    ['IntProf', 'intelligence'],
    ['WisProf', 'wisdom'],
    ['ChaProf', 'charisma'],
  ];
  const proficiencies: Record<string, string> = {};
  for (const [fieldName, ability] of saveProfKeys) {
    if (fields[fieldName] === '•' || fields[fieldName] === 'P') {
      proficiencies[`saving-throw-${ability}`] = 'proficiency';
    }
  }

  // ── Skill proficiencies ──
  const skillProfKeys: [string, string][] = [
    ['AcrobaticsProf', 'acrobatics'],
    ['AnimalProf', 'animal-handling'],
    ['ArcanaProf', 'arcana'],
    ['AthleticsProf', 'athletics'],
    ['DeceptionProf', 'deception'],
    ['HistoryProf', 'history'],
    ['InsightProf', 'insight'],
    ['IntimidationProf', 'intimidation'],
    ['InvestigationProf', 'investigation'],
    ['MedicineProf', 'medicine'],
    ['NatureProf', 'nature'],
    ['PerceptionProf', 'perception'],
    ['PerformanceProf', 'performance'],
    ['PersuasionProf', 'persuasion'],
    ['ReligionProf', 'religion'],
    ['SleightofHandProf', 'sleight-of-hand'],
    ['StealthProf', 'stealth'],
    ['SurvivalProf', 'survival'],
  ];
  for (const [fieldName, skill] of skillProfKeys) {
    if (fields[fieldName] === 'P' || fields[fieldName] === '•') {
      proficiencies[`skill-${skill}`] = 'proficiency';
    }
  }

  // ── Feats (from FeaturesTraits fields) ──
  const feats = parseFeats(fields);

  // ── Subclass (from FeaturesTraits fields) ──
  const subclass = parseSubclass(fields);

  // ── Spells ──
  const spells = parseSpells(fields, pageTexts, warnings);

  // ── Race ──
  const race = fields['Race'] ?? fields['RACE'] ?? undefined;

  // ── Background ──
  const background = fields['Background'] ?? fields['BACKGROUND'] ?? undefined;

  // ── Alignment ──
  const alignment = fields['Alignment'] ?? fields['ALIGNMENT'] ?? undefined;

  // ── Armor Class ──
  const acRaw = fields['AC'] ?? fields['ArmorClass'] ?? '';
  const armorClass = parseInt(acRaw, 10) || undefined;

  // ── Walk Speed ──
  const speedRaw = fields['Speed'] ?? fields['SPEED'] ?? '';
  const walkSpeed = parseInt(speedRaw, 10) || undefined;

  // ── Languages & Other Proficiencies ──
  // DDB PDFs put languages/tool/weapon/armor proficiencies in "ProficienciesLang".
  const languages = parseLanguages(fields['ProficienciesLang'] ?? '');

  // ── Currency ──
  // DDB PDFs have individual CP, SP, EP, GP, PP fields.
  // We don't persist these to the character model (wallet is on the party doc)
  // but we log them so the DM can manually enter them.
  const cp = parseInt(fields['CP'] ?? '', 10) || 0;
  const sp = parseInt(fields['SP'] ?? '', 10) || 0;
  const ep = parseInt(fields['EP'] ?? '', 10) || 0;
  const gp = parseInt(fields['GP'] ?? '', 10) || 0;
  const pp = parseInt(fields['PP'] ?? '', 10) || 0;
  if (cp + sp + ep + gp + pp > 0) {
    warnings.push(
      `Currency found: ${cp} CP, ${sp} SP, ${ep} EP, ${gp} GP, ${pp} PP — add to party wallet manually`,
    );
  }

  // ── Personality / Ideals / Bonds / Flaws ──
  const personalityTraits = fields['PersonalityTraits'] ?? fields['Personality Traits'] ?? undefined;
  const ideals = fields['Ideals'] ?? undefined;
  const bonds = fields['Bonds'] ?? undefined;
  const flaws = fields['Flaws'] ?? undefined;

  // ── Spellcasting metadata ──
  const spellcastingAbility = fields['SpellcastingAbility'] ?? fields['Spellcasting Ability'] ?? undefined;
  const spellSaveDcRaw = fields['SpellSaveDC'] ?? fields['Spell Save DC'] ?? '';
  const spellSaveDc = parseInt(spellSaveDcRaw, 10) || undefined;
  const spellAtkRaw = fields['SpellAtkBonus'] ?? fields['Spell Atk Bonus'] ?? fields['SAB'] ?? '';
  const spellAttackBonus = parseInt(spellAtkRaw, 10) || undefined;

  // ── Expertise detection ──
  // DDB PDFs mark expertise with 'E' instead of 'P' or '•' on skill prof fields.
  // Re-scan skill fields for expertise markers.
  for (const [fieldName, skill] of skillProfKeys) {
    if (fields[fieldName] === 'E') {
      proficiencies[`skill-${skill}`] = 'expertise';
    }
  }

  // ── Equipment & Weapons → inventory ──
  const inventory: { name: string; qty: number }[] = [];
  const equipmentText = fields['Equipment'] ?? '';
  if (equipmentText.trim()) {
    for (const line of equipmentText.split('\n')) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.length < 2) continue;
      // Try to parse "2x Potion of Healing" or "Rope (50 ft.)" patterns.
      const qtyMatch = trimmed.match(/^(\d+)\s*[x×]\s+(.+)/i);
      if (qtyMatch) {
        inventory.push({ name: qtyMatch[2].trim(), qty: parseInt(qtyMatch[1], 10) || 1 });
      } else {
        inventory.push({ name: trimmed, qty: 1 });
      }
    }
  }
  // DDB PDFs use "Wpn Name", "Wpn Name 2", "Wpn Name 3" for weapon names.
  for (const key of ['Wpn Name', 'Wpn Name 2', 'Wpn Name 3']) {
    const name = fields[key]?.trim();
    if (name && !inventory.some(i => i.name.toLowerCase() === name.toLowerCase())) {
      inventory.push({ name, qty: 1 });
    }
  }
  if (inventory.length > 0) {
    warnings.push(
      `Inventory: ${inventory.length} items imported (${inventory.map(i => i.name).join(', ')})`,
    );
  }

  return {
    className: classLevel.className,
    subclass: subclass ?? undefined,
    level: classLevel.level,
    hitDieTotal,
    baseStats,
    asiDeltas: {}, // Can't distinguish base from ASI in a PDF export.
    featDeltas: {}, // Ditto.
    proficiencies,
    feats,
    spells,
    inventory,
    race,
    background,
    alignment,
    walkSpeed,
    languages: languages.length > 0 ? languages : undefined,
    personalityTraits,
    ideals,
    bonds,
    flaws,
    armorClass,
    spellcastingAbility,
    spellSaveDc,
    spellAttackBonus,
    warnings,
  };
}

// ────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────

function parseClassLevel(
  raw: string,
): { className: string; level: number } | null {
  // "Wizard 5", "Fighter 3 / Rogue 2", etc.
  const classPattern =
    /\b(Artificer|Barbarian|Bard|Blood\s*Hunter|Cleric|Druid|Fighter|Monk|Paladin|Ranger|Rogue|Sorcerer|Warlock|Wizard)\s+(\d{1,2})\b/i;
  const m = raw.match(classPattern);
  if (m) {
    return {
      className: m[1].charAt(0).toUpperCase() + m[1].slice(1).toLowerCase(),
      level: parseInt(m[2], 10),
    };
  }
  return null;
}

function parseHitDice(raw: string): { count: number; die: number } | null {
  // "5d6", "3d10", etc.
  const m = raw.match(/(\d+)d(\d+)/i);
  if (m) return { count: parseInt(m[1], 10), die: parseInt(m[2], 10) };
  return null;
}

/**
 * Known 5e languages for filtering out non-language proficiencies
 * from the combined "ProficienciesLang" field.
 */
const KNOWN_LANGUAGES = new Set([
  'common', 'dwarvish', 'elvish', 'giant', 'gnomish', 'goblin',
  'halfling', 'orc', 'abyssal', 'celestial', 'draconic', 'deep speech',
  'infernal', 'primordial', 'sylvan', 'undercommon', 'druidic',
  "thieves' cant", 'thieves cant', 'auran', 'aquan', 'ignan', 'terran',
]);

/**
 * Parse language names from the DDB PDF "ProficienciesLang" field.
 * This field contains a mix of languages, tool proficiencies, weapon
 * proficiencies, and armor proficiencies — we filter for known languages.
 */
function parseLanguages(raw: string): string[] {
  if (!raw.trim()) return [];
  const languages: string[] = [];
  // Split on commas, semicolons, or newlines.
  const parts = raw.split(/[,;\n]/).map(s => s.trim()).filter(Boolean);
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (KNOWN_LANGUAGES.has(lower)) {
      // Title-case it.
      languages.push(part.charAt(0).toUpperCase() + part.slice(1));
    }
  }
  return languages;
}

/** Extract feat names from the FeaturesTraits form fields. */
function parseFeats(fields: Record<string, string>): string[] {
  const feats: string[] = [];
  // Combine all FeaturesTraits fields.
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Look for the "=== FEATS ===" section.
  const featsSection = traitText.match(
    /===\s*FEATS\s*===\s*\n([\s\S]*?)(?:===|$)/i,
  );
  if (featsSection) {
    const lines = featsSection[1].split('\n');
    for (const line of lines) {
      // Feat entries: "* Feat Name • Source"
      const m = line.match(/\*\s+(.+?)(?:\s*•\s*.+)?$/);
      if (m) {
        const name = m[1].trim();
        if (name && !name.startsWith('|') && name.length > 2) {
          feats.push(name);
        }
      }
    }
  }
  return feats;
}

/** Try to extract the subclass from the FeaturesTraits fields. */
function parseSubclass(fields: Record<string, string>): string | null {
  const traitText = [
    fields['FeaturesTraits1'],
    fields['FeaturesTraits2'],
    fields['FeaturesTraits3'],
  ]
    .filter(Boolean)
    .join('\n');

  // Common DDB patterns: "| School of Necromancy", "| Path of the Berserker", etc.
  const patterns = [
    /\|\s*(School\s+of\s+\w+)/i,
    /\|\s*(College\s+of\s+\w+)/i,
    /\|\s*(Path\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Circle\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Oath\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(\w[\w\s]+?Domain)/i,
    /\|\s*(Way\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(Order\s+of\s+(?:the\s+)?\w+)/i,
    /\|\s*(The\s+\w+(?:\s+Patron)?)/i,
  ];
  for (const p of patterns) {
    const m = traitText.match(p);
    if (m) return m[1].trim();
  }
  return null;
}

/** Metadata classification for a DDB PDF spell field value. */
type SpellFieldType =
  | 'name'
  | 'components'
  | 'range'
  | 'duration'
  | 'castingTime'
  | 'source'      // source book reference (PHB 259, XGtE 169, etc.)
  | 'sourceClass'  // class or feature that grants the spell
  | 'header'
  | 'skip';        // checkbox, number, compact notation, etc.

/**
 * Classify a DDB PDF spell field value so we can attach metadata to the
 * parent spell instead of importing it as a separate spell entry.
 *
 * DDB PDF spell pages store each spell as ~6 consecutive fields:
 *   name, class, range, components, duration, source
 */
function classifySpellField(val: string): SpellFieldType {
  const v = val.trim();
  if (!v || v.length < 2) return 'skip';

  // ── Headers & dividers ──
  if (/^={2,}/.test(v)) return 'header';

  // ── Pure numbers, checkboxes, booleans ──
  if (/^\d+$/.test(v)) return 'skip';
  if (/^(Yes|No|True|False|On|Off|•|P|X|✓|✗)$/i.test(v)) return 'skip';

  // ── Compact notation: "D: 1m, V/S" — contains both duration and components ──
  if (/^D:\s/i.test(v)) return 'skip'; // mixed; hard to split reliably

  // ── Components: V, S, M and combinations ──
  if (/^[VSM][,\/\s]*[VSM]?[,\/\s]*[VSM]?$/i.test(v) && v.length <= 7) return 'components';

  // ── Ranges ──
  if (/^(Touch|Self|Sight|Unlimited|Special)$/i.test(v)) return 'range';
  if (/^\d+\s*(ft\.?|feet|miles?)\s*$/i.test(v)) return 'range';

  // ── Durations ──
  if (/^(Instantaneous|Concentration)/i.test(v)) return 'duration';
  if (/^\d+\s*(minutes?|hours?|rounds?|days?|weeks?|years?)\s*$/i.test(v)) return 'duration';
  if (/^up to \d/i.test(v)) return 'duration';

  // ── Casting times ──
  if (/^(1\s*action|1\s*bonus\s*action|1\s*reaction|ritual|1\s*minute|10\s*minutes?)\s*$/i.test(v)) return 'castingTime';

  // ── Source books: "PHB 259", "XGtE 169", "PHB-2024 333" ──
  if (/^(PHB|DMG|XGtE|TCoE|VGtM|MToF|SCAG|FToD|SCoC|MotM|BGG|EGtW|IDRotF|AI|GGtR|MOoT|SatO|ERLW|WBtW|CRCotN|JttRC|KGV|AAG|SAiS|SjA|PHB-\d{4})\s*\d*$/i.test(v)) return 'source';

  // ── Class names ──
  if (/^(Artificer|Barbarian|Bard|Blood\s*Hunter|Cleric|Druid|Fighter|Monk|Paladin|Ranger|Rogue|Sorcerer|Warlock|Wizard)$/i.test(v)) return 'sourceClass';

  // ── Feature / source feat names ──
  if (/^(Innate Spellcasting|Spellcasting|Magic Initiate|Ritual Caster|Pact Magic)/i.test(v)) return 'sourceClass';
  if (/^Magic Initiate\s*\(/i.test(v)) return 'sourceClass';

  // ── Anything else is treated as a spell name ──
  return 'name';
}

/** Attach a classified metadata value to a SpellEntry. */
function attachMeta(spell: SpellEntry, val: string, type: SpellFieldType) {
  switch (type) {
    case 'components':  spell.components = spell.components ? `${spell.components}, ${val}` : val; break;
    case 'range':       spell.range      = val; break;
    case 'duration':    spell.duration   = val; break;
    case 'castingTime': spell.castingTime = val; break;
    case 'source':      spell.source     = spell.source ? `${spell.source}; ${val}` : val; break;
    case 'sourceClass': spell.source     = spell.source ? `${spell.source}; ${val}` : val; break;
    // 'header', 'skip', 'name' — don't attach.
  }
}

/**
 * Extract spells from DDB PDF form fields.
 *
 * DDB PDFs store spells across multiple form field naming patterns:
 * - "Spells 1018", "Spells 1019", etc. — individual spell fields (name + metadata)
 * - "Spells1", "Spells2" — textarea blocks with spell lists
 * - "SL#" + "SPName#" — numbered spell entries with level prefix
 *
 * Each spell in the numbered pattern spans ~6 consecutive fields (name,
 * class, range, components, duration, source). isSpellMetadata() filters
 * out the non-name fields. The field name ranges approximate DDB's
 * cantrip/level groupings — we assign levels based on the range.
 */
function parseSpells(
  fields: Record<string, string>,
  pageTexts: string[],
  warnings: string[],
): SpellEntry[] {
  const spells: SpellEntry[] = [];
  const seen = new Set<string>();

  /** Add a fully-built spell if not a duplicate. */
  const commit = (spell: SpellEntry) => {
    const key = spell.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    spells.push(spell);
  };

  // ── Pattern 1: Consecutive "Spells XXXX" form fields ──
  // DDB PDFs store each spell as ~6 consecutive numbered fields:
  //   name, class, range, components, duration, source
  // We read them in order. Every time we encounter a 'name' field, we
  // start a new spell; subsequent metadata fields attach to it.
  const spellRanges: [number, number, number][] = [
    [1014, 1021, 0], // cantrips
    [1022, 1035, 1], // level 1
    [1036, 1048, 2], // level 2
    [1049, 1061, 3], // level 3
    [1062, 1074, 4], // level 4
    [1075, 1087, 5], // level 5
    [1088, 1098, 6], // level 6
    [1099, 1107, 7], // level 7
    [1108, 1114, 8], // level 8
    [1115, 1121, 9], // level 9
  ];

  for (const [start, end, level] of spellRanges) {
    let current: SpellEntry | null = null;
    for (let n = start; n <= end; n++) {
      const val = fields[`Spells ${n}`]?.trim();
      if (!val) continue;
      const type = classifySpellField(val);
      if (type === 'header') continue;
      if (type === 'name') {
        // Commit the previous spell, start a new one.
        if (current) commit(current);
        current = { name: val, level, prepared: true };
      } else if (current) {
        // Attach metadata to the current spell.
        attachMeta(current, val, type);
      }
      // If no current spell yet, skip stray metadata.
    }
    if (current) commit(current);
  }

  // ── Pattern 2: Generic "SpellName#" fields ──
  for (const [key, val] of Object.entries(fields)) {
    const m = key.match(/^SpellName(\d+)$/);
    if (!m || !val?.trim()) continue;
    const lvlStr = fields[`SL${m[1]}`];
    const level = lvlStr ? parseInt(lvlStr, 10) || 0 : 0;
    const name = val.trim();
    if (classifySpellField(name) === 'name') {
      commit({ name, level, prepared: true });
    }
  }

  // ── Pattern 3: "Spells1", "Spells2" — block text, one spell per line ──
  for (let block = 1; block <= 5; block++) {
    const text = fields[`Spells${block}`];
    if (!text) continue;
    for (const line of text.split('\n')) {
      const trimmed = line.replace(/^[\-\*•]\s*/, '').trim();
      if (!trimmed || trimmed.length < 2) continue;
      if (/^(cantrip|level|---)/i.test(trimmed)) continue;
      if (classifySpellField(trimmed) === 'name') {
        commit({ name: trimmed, level: 0, prepared: true });
      }
    }
  }

  // ── Pattern 4: Broad catch-all — any "spell" field, grouped ──
  // Collect all spell-keyed field values in field-name order and group them
  // the same way as Pattern 1.
  {
    const spellFieldEntries = Object.entries(fields)
      .filter(([k]) => /spell/i.test(k))
      .filter(([k]) => !/ability|save|dc|bonus|attack|slot|level|class/i.test(k))
      .filter(([, v]) => v.trim().length >= 2);
    // Sort by field name to maintain grouping order.
    spellFieldEntries.sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
    let current: SpellEntry | null = null;
    for (const [, val] of spellFieldEntries) {
      const v = val.trim();
      const type = classifySpellField(v);
      if (type === 'header' || type === 'skip') continue;
      if (type === 'name') {
        if (current) commit(current);
        current = { name: v, level: 0, prepared: true };
      } else if (current) {
        attachMeta(current, v, type);
      }
    }
    if (current) commit(current);
  }

  // ── Pattern 5: Text-content fallback (no form fields found spells) ──
  if (spells.length === 0) {
    for (const pageText of pageTexts) {
      if (!/\b(cantrip|spell\s*slot|spellcasting)\b/i.test(pageText)) continue;
      const levelHeaders = pageText.matchAll(
        /(?:cantrips|(\d)\w*\s*level)/gi,
      );
      let currentLevel = 0;
      let lastIndex = 0;
      for (const hdr of levelHeaders) {
        const idx = hdr.index ?? 0;
        if (idx < lastIndex) continue;
        currentLevel = hdr[1] ? parseInt(hdr[1], 10) : 0;
        lastIndex = idx;
        const nextSection = pageText.slice(idx + hdr[0].length, idx + hdr[0].length + 500);
        const candidates = nextSection.match(
          /\b[A-Z][a-z]+(?:\s+[A-Za-z']+){0,5}\b/g,
        );
        if (!candidates) continue;
        for (const c of candidates) {
          const name = c.trim();
          if (name.length < 4) continue;
          if (/^(Level|Spell|Slot|Cast|Save|Attack|Bonus|Class|Page|Total)/i.test(name)) continue;
          if (classifySpellField(name) === 'name') {
            commit({ name, level: currentLevel, prepared: true });
          }
        }
      }
    }
    if (spells.length > 0) {
      warnings.push(
        `${spells.length} spells extracted from PDF text (not form fields) — verify names and levels`,
      );
    }
  }

  if (spells.length === 0) {
    // Log spell-related field names for diagnostics.
    const spellFields = Object.keys(fields).filter((k) => /spell/i.test(k));
    if (spellFields.length > 0) {
      console.warn('[PDF import] Found spell-related fields but extracted 0 spells:', spellFields);
    } else {
      console.warn('[PDF import] No spell-related form fields found in PDF — spell page may be missing from export');
    }
    warnings.push(
      'No spells found in PDF — if this is a caster, check the PDF includes a spell sheet page',
    );
  }

  return spells;
}

const CLASS_HIT_DICE: Record<string, number> = {
  barbarian: 12,
  fighter: 10,
  paladin: 10,
  ranger: 10,
  bard: 8,
  cleric: 8,
  druid: 8,
  monk: 8,
  rogue: 8,
  warlock: 8,
  'blood hunter': 10,
  artificer: 8,
  sorcerer: 6,
  wizard: 6,
};
